import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Heading,
  Container,
  Table,
  TableRow,
  TableData,
  LoadingContainer,
  LoadingSpinner,
  Input,
  Select,
  FlexContainer,
  ModalOverlay,  // For the modal overlay styling
  ModalContent,  // For the modal content styling
  FieldContainer,
  ListContainer
} from '../../styled/common';
import StockDetailTable from './StockDetailTable';
import FaultyStockDetailTable from './FaultyStockDetailTable';

const AggregatedStockTable = ({user}) => {
  const [aggregatedData, setAggregatedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [isFaultyStock, setIsFaultyStock] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterState, setSelectedFilterState] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal open/close
  const [stateOptions, setStateOptions] = useState([]); // Store unique states

  useEffect(() => {
    const fetchAggregatedData = async () => {
      setIsLoading(true);
      try {
        let response;
        if (user.role === 'pm') {
          response = await axios.get('/api/warehouse-stock/');
        } else if (user.role === 'warehouseAdmin') {
          response = await axios.get(`/api/warehouse-stock/?state=${user.stateLocation}`);
        }
        
        const data = response.data;
        setAggregatedData(data);
        setFilteredData(data);

        // Extract unique states dynamically
        const uniqueStates = [...new Set(data.map((item) => item.state))];
        setStateOptions(uniqueStates); // Update state options for the filter
      } catch (error) {
        console.error('Error fetching aggregated data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAggregatedData();
  }, []);

  useEffect(() => {
    const filtered = aggregatedData.filter((item) => {
      const matchesMaterial = String(item.material_id.Material)
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesState = selectedFilterState
        ? item.state === selectedFilterState
        : true;
      return matchesMaterial && matchesState;
    });
    setFilteredData(filtered);
  }, [searchTerm, selectedFilterState, aggregatedData]);

  const handleRowClick = (material, state, isFaulty = false) => {
    setSelectedMaterial(material);
    setSelectedState(state);
    setIsFaultyStock(isFaulty);
    setIsModalOpen(true); // Open modal when a row is clicked
  };

  const handleCloseDetail = () => {
    setSelectedMaterial(null);
    setSelectedState(null);
    setIsFaultyStock(false);
    setIsModalOpen(false); // Close modal
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <ListContainer style={{ flex: '1 1 100%', maxWidth: '100%' }}>
        <Heading>Warehouse Aggregated Inventory by State</Heading>
        <FlexContainer
          direction="column"
          justify="flex-start"
          align="left"
          gap="20px"
          style={{
            marginBottom: '20px',
            padding: '10px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            
          }}
        >
          <FieldContainer>
            <label htmlFor="materialSearch">Search by Material:</label>
            <Input
              id="materialSearch"
              type="text"
              placeholder="Search by material"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FieldContainer>

          <FieldContainer>
            <label htmlFor="stateFilter">Filter by State:</label>
            <Select
              id="stateFilter"
              value={selectedFilterState}
              onChange={(e) => setSelectedFilterState(e.target.value)}
            >
              <option value="">All States</option>
              {stateOptions.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </FieldContainer>
        </FlexContainer>

        <Table>
          <thead>
            <tr>
              <th>Material</th>
              <th>State</th>
              <th>Total Working Stock Quantity</th>
              <th>Total Faulty Stock Quantity</th>
            </tr>
          </thead>
          <tbody>
            {filteredData
              .filter((item) => item.totalSurplusStock !== 0 || item.totalFaultyStock !== 0) // Exclude rows with both quantities as 0
              .map((item) => (
                <TableRow key={`${item._id}-${item.state}`}>
                  <TableData data-label="Material">
                    {item.material_id.Material} - {item.material_id.MaterialDescription}
                  </TableData>
                  <TableData data-label="State">{item.state}</TableData>
                  <TableData
                    data-label="Total Working Stock Quantity"
                    onClick={() => handleRowClick(item.material_id._id, item.state, false)}
                  >
                    {item.totalSurplusStock}
                  </TableData>
                  <TableData
                    data-label="Total Faulty Stock Quantity"
                    onClick={() => handleRowClick(item.material_id._id, item.state, true)}
                  >
                    {item.totalFaultyStock}
                  </TableData>
                </TableRow>
            ))}
          </tbody>
        </Table>
      </ListContainer>
      
      {/* Modal implementation */}
      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            {selectedMaterial && selectedState && (
              isFaultyStock ? (
                <FaultyStockDetailTable
                  user={user}
                  material={selectedMaterial}
                  state={selectedState}
                  onClose={handleCloseDetail}
                />
              ) : (
                <StockDetailTable
                  user={user}
                  material={selectedMaterial}
                  state={selectedState}
                  onClose={handleCloseDetail}
                />
              )
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AggregatedStockTable;
